import { useState, useMemo, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ragApi, RagApiError } from '../services/ApiService'
import type {
  Document,
  URL,
  DocumentUploadData,
  URLUploadData,
  ApiDocument,
} from '../lib/types'
import { DataTransformers } from '../lib/types'
import { queryKeys } from '@/queries/keys'
import { useComponentLogger } from './use-logger'

type DocumentUpdateData = Omit<Partial<Document>, 'file' | 'category' | 'subcategory'> & {
  category?: string | null
  subcategory?: string | null
  file?: File
}

type URLUpdateData = Omit<Partial<URL>, 'category' | 'subcategory'> & {
  category?: string | null
  subcategory?: string | null
}

interface UseDocumentsResult {
  documents: Document[]
  loading: boolean
  uploadLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  /** Devuelve el documento creado: quien sube necesita su identificador
   *  para poder enseñar lo que el agente saque de el (4.6). */
  uploadDocument: (data: DocumentUploadData) => Promise<Document>
  deleteDocument: (id: string) => Promise<void>
  updateDocument: (id: string, data: DocumentUpdateData) => Promise<void>
}

interface UseURLsResult {
  urls: URL[]
  loading: boolean
  uploadLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  uploadURL: (data: URLUploadData) => Promise<void>
  deleteURL: (id: string) => Promise<void>
  updateURL: (id: string, data: URLUpdateData) => Promise<void>
}

const parseOptionalNumericId = (value?: string | null): number | undefined => {
  if (!value || value.trim() === '') return undefined
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

function normalizeDocumentPatch(data: DocumentUpdateData): Partial<Document> {
  const rest = { ...data } as Omit<DocumentUpdateData, 'file'>
  delete (rest as { file?: File }).file
  return {
    ...rest,
    category: data.category ?? rest.category ?? '',
    subcategory: data.subcategory ?? rest.subcategory ?? '',
  }
}

function normalizeUrlPatch(data: URLUpdateData): Partial<URL> {
  const { category, subcategory, ...rest } = data
  return {
    ...rest,
    category: category ?? '',
    subcategory: subcategory ?? '',
  }
}

function useRawDocumentsQuery() {
  return useQuery({
    queryKey: queryKeys.documents.raw(),
    queryFn: async () => {
      const response = (await ragApi.getDocuments()) as ApiDocument[]
      return Array.isArray(response) ? response : []
    },
    staleTime: 60_000,
    refetchOnMount: false,
    retry: 0,
    refetchInterval: (query) => {
      const docs = query.state.data as ApiDocument[] | undefined
      const hasProcessing = docs?.some((doc) => doc.status === 'pending' || doc.status === 'processing')
      return hasProcessing ? 10_000 : false
    },
  })
}

/**
 * Hook for managing documents (PDFs, TXT, CSV files)
 */
export function useDocuments(): UseDocumentsResult {
  const logger = useComponentLogger('useDocuments')
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const rawQuery = useRawDocumentsQuery()

  const documents = useMemo(
    () =>
      (rawQuery.data || [])
        .filter((doc) => doc.document_type !== 'url')
        .map((doc) => DataTransformers.apiDocumentToDocument(doc)),
    [rawQuery.data]
  )

  const uploadDocumentMutation = useMutation({
    mutationFn: async (data: DocumentUploadData) => {
      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('document_type', data.document_type)
      if (data.description) formData.append('description', data.description)
      if (data.category) formData.append('category', data.category)
      if (data.subcategory) formData.append('subcategory', data.subcategory)
      if (data.directory) formData.append('directory', data.directory)
      if (data.file) formData.append('file', data.file)
      formData.append('status', 'pending')
      const apiResponse = (await ragApi.createDocument(formData)) as ApiDocument
      return DataTransformers.apiDocumentToDocument(apiResponse)
    },
    onSuccess: (newDocument) => {
      queryClient.setQueryData<ApiDocument[]>(queryKeys.documents.raw(), (old) => {
        const next = Array.isArray(old) ? [...old] : []
        const id = Number(newDocument.id)
        const existing = next.findIndex((doc) => doc.id === id)
        if (existing >= 0) {
          next[existing] = { ...next[existing], ...newDocument } as unknown as ApiDocument
          return next
        }
        return [
          {
            ...(newDocument as unknown as ApiDocument),
            id,
          },
          ...next,
        ]
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents.raw() })
    },
  })

  const deleteDocumentMutation = useMutation({
    mutationFn: async (id: string) => {
      await ragApi.deleteDocument(id)
      return id
    },
    onSuccess: (id) => {
      queryClient.setQueryData<ApiDocument[]>(queryKeys.documents.raw(), (old) =>
        (old || []).filter((doc) => String(doc.id) !== id)
      )
    },
  })

  const updateDocumentMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DocumentUpdateData }) => {
      const apiData: Record<string, unknown> = {}
      if (data.title && data.title.trim() !== '') apiData.title = data.title
      if (data.description && data.description.trim() !== '') apiData.description = data.description

      const categoryId = parseOptionalNumericId(data.category)
      const subcategoryId = parseOptionalNumericId(data.subcategory)
      const directoryId = parseOptionalNumericId(data.directory)
      const categoryProvided = Object.prototype.hasOwnProperty.call(data, 'category')
      const subcategoryProvided = Object.prototype.hasOwnProperty.call(data, 'subcategory')

      if (categoryProvided) apiData.category = categoryId ?? null
      if (subcategoryProvided) apiData.subcategory = subcategoryId ?? null
      if (data.directory === null) apiData.directory = null
      else if (directoryId) apiData.directory = directoryId

      const replacementFile = data.file
      const hasReplacementFile = replacementFile instanceof File
      const payload: Record<string, unknown> | FormData = hasReplacementFile
        ? (() => {
            const formData = new FormData()
            Object.entries(apiData).forEach(([key, value]) => {
              if (value === undefined) return
              formData.append(key, value === null ? '' : String(value))
            })
            formData.append('file', replacementFile)
            return formData
          })()
        : apiData

      if (Object.keys(apiData).length === 0 && !hasReplacementFile) {
        return null
      }

      await ragApi.updateDocument(id, payload)
      try {
        const updatedDocument = (await ragApi.getDocument(id)) as ApiDocument
        return DataTransformers.apiDocumentToDocument(updatedDocument)
      } catch (refreshError) {
        logger.warn('Failed to refresh document after update, using local patch', refreshError)
        const { file: ignoredFile, ...safeData } = data
        void ignoredFile
        return { id, ...normalizeDocumentPatch(safeData) } as Document
      }
    },
    onSuccess: (updatedDocument) => {
      if (!updatedDocument) return
      queryClient.setQueryData<ApiDocument[]>(queryKeys.documents.raw(), (old) =>
        (old || []).map((doc) =>
          String(doc.id) === String(updatedDocument.id)
            ? ({ ...doc, ...(updatedDocument as unknown as ApiDocument) } as ApiDocument)
            : doc
        )
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents.raw() })
    },
  })

  const uploadDocument = useCallback(async (data: DocumentUploadData) => {
    try {
      setError(null)
      return await uploadDocumentMutation.mutateAsync(data)
    } catch (err) {
      logger.warn('Document upload failed', err)
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to upload document'
      throw new Error(errorMessage)
    }
  }, [logger, uploadDocumentMutation])

  const deleteDocument = useCallback(async (id: string) => {
    try {
      setError(null)
      await deleteDocumentMutation.mutateAsync(id)
    } catch (err) {
      logger.error('Error deleting document', err)
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to delete document'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [deleteDocumentMutation, logger])

  const updateDocument = useCallback(async (id: string, data: DocumentUpdateData) => {
    try {
      setError(null)
      await updateDocumentMutation.mutateAsync({ id, data })
    } catch (err) {
      logger.error('Error updating document', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update document'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [logger, updateDocumentMutation])

  return {
    documents,
    loading: rawQuery.isLoading,
    uploadLoading: uploadDocumentMutation.isPending || updateDocumentMutation.isPending,
    error: error || (rawQuery.error instanceof Error ? rawQuery.error.message : null),
    refetch: async () => {
      await rawQuery.refetch()
    },
    uploadDocument,
    deleteDocument,
    updateDocument,
  }
}

/**
 * Hook for managing URLs
 */
export function useURLs(categories?: { id: string; name: string; subcategories: { id: string; name: string }[] }[]): UseURLsResult {
  const logger = useComponentLogger('useURLs')
  void categories
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const rawQuery = useRawDocumentsQuery()

  const urls = useMemo(
    () =>
      (rawQuery.data || [])
        .filter((doc) => doc.document_type === 'url')
        .map((doc) => DataTransformers.apiDocumentToURL(doc)),
    [rawQuery.data]
  )

  const uploadURLMutation = useMutation({
    mutationFn: async (data: URLUploadData) => {
      const apiData = DataTransformers.urlUploadToApiData(data) as Record<string, unknown>
      const apiResponse = (await ragApi.processURL(apiData)) as ApiDocument
      return DataTransformers.apiDocumentToURL(apiResponse)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents.raw() })
    },
  })

  const deleteURLMutation = useMutation({
    mutationFn: async (id: string) => {
      await ragApi.deleteDocument(id)
      return id
    },
    onSuccess: (id) => {
      queryClient.setQueryData<ApiDocument[]>(queryKeys.documents.raw(), (old) =>
        (old || []).filter((doc) => String(doc.id) !== id)
      )
    },
  })

  const updateURLMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: URLUpdateData }) => {
      const apiData: Record<string, unknown> = {}
      if (data.title && data.title.trim() !== '') apiData.title = data.title
      if (data.description && data.description.trim() !== '') apiData.description = data.description
      if (data.url && data.url.trim() !== '') apiData.url = data.url
      apiData.document_type = 'url'

      const resolvedCategoryId = parseOptionalNumericId(data.category)
      const resolvedSubcategoryId = parseOptionalNumericId(data.subcategory)
      const directoryId = parseOptionalNumericId(data.directory)
      const categoryProvided = Object.prototype.hasOwnProperty.call(data, 'category')
      const subcategoryProvided = Object.prototype.hasOwnProperty.call(data, 'subcategory')

      if (categoryProvided) apiData.category = resolvedCategoryId ?? null
      if (subcategoryProvided) apiData.subcategory = resolvedSubcategoryId ?? null
      if (data.directory === null) apiData.directory = null
      else if (directoryId) apiData.directory = directoryId

      if (Object.keys(apiData).length === 0) return null

      await ragApi.updateDocument(id, apiData)

      try {
        const updatedDocument = (await ragApi.getDocument(id)) as ApiDocument
        return DataTransformers.apiDocumentToURL(updatedDocument)
      } catch (refreshError) {
        logger.warn('Failed to refresh URL after update, using local patch', refreshError)
        return { id, ...normalizeUrlPatch(data) } as URL
      }
    },
    onSuccess: (updatedURL) => {
      if (!updatedURL) return
      queryClient.setQueryData<ApiDocument[]>(queryKeys.documents.raw(), (old) =>
        (old || []).map((doc) =>
          String(doc.id) === String(updatedURL.id)
            ? ({ ...doc, ...(updatedURL as unknown as ApiDocument) } as ApiDocument)
            : doc
        )
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents.raw() })
    },
  })

  const uploadURL = useCallback(async (data: URLUploadData) => {
    try {
      setError(null)
      await uploadURLMutation.mutateAsync(data)
    } catch (err) {
      logger.error('Error uploading URL', err)
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to upload URL'
      throw new Error(errorMessage)
    }
  }, [logger, uploadURLMutation])

  const deleteURL = useCallback(async (id: string) => {
    try {
      setError(null)
      await deleteURLMutation.mutateAsync(id)
    } catch (err) {
      logger.error('Error deleting URL', err)
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to delete URL'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [deleteURLMutation, logger])

  const updateURL = useCallback(async (id: string, data: URLUpdateData) => {
    try {
      setError(null)
      await updateURLMutation.mutateAsync({ id, data })
    } catch (err) {
      logger.error('Error updating URL', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update URL'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [logger, updateURLMutation])

  return {
    urls,
    loading: rawQuery.isLoading,
    uploadLoading: uploadURLMutation.isPending || updateURLMutation.isPending,
    error: error || (rawQuery.error instanceof Error ? rawQuery.error.message : null),
    refetch: async () => {
      await rawQuery.refetch()
    },
    uploadURL,
    deleteURL,
    updateURL,
  }
}

/**
 * Hook for combined documents and URLs management
 */
export function useAllDocuments(categories?: { id: string; name: string; subcategories: { id: string; name: string }[] }[]) {
  const documents = useDocuments()
  const urls = useURLs(categories)

  return {
    documents: documents.documents,
    urls: urls.urls,
    loading: documents.loading || urls.loading,
    uploadLoading: documents.uploadLoading || urls.uploadLoading,
    error: documents.error || urls.error,
    refetch: async () => {
      await Promise.all([documents.refetch(), urls.refetch()])
    },
    uploadDocument: documents.uploadDocument,
    uploadURL: urls.uploadURL,
    deleteDocument: documents.deleteDocument,
    deleteURL: urls.deleteURL,
    updateDocument: documents.updateDocument,
    updateURL: urls.updateURL,
  }
}

/**
 * Hook for search functionality
 */
export function useDocumentSearch() {
  const logger = useComponentLogger('useDocumentSearch')

  const [results, setResults] = useState<unknown[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const search = useCallback(async (query: string, limit?: number, filters?: object) => {
    if (!query.trim()) {
      setResults([])
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = (await ragApi.searchDocuments(query, limit, filters as Record<string, unknown>)) as {
        results: unknown[]
      }
      setResults(response.results || [])
    } catch (err) {
      logger.error('Error searching documents', err)
      setError(err instanceof RagApiError ? err.message : 'Search failed')
    } finally {
      setLoading(false)
    }
  }, [logger])

  const generateResponse = useCallback(
    async (
      question: string,
      conversationHistory?: Array<{ role: string; content: string }>,
      limit?: number
    ) => {
      try {
        setLoading(true)
        setError(null)

        const response = await ragApi.generateResponse(question, conversationHistory, limit)
        return response
      } catch (err) {
        logger.error('Error generating response', err)
        const errorMessage = err instanceof RagApiError ? err.message : 'Failed to generate response'
        setError(errorMessage)
        throw new Error(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [logger]
  )

  return {
    results,
    loading,
    error,
    search,
    generateResponse,
    clearResults: () => setResults([]),
  }
}
